import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { deDE } from "@mui/x-date-pickers/locales";
import { initSentry } from "./sentry";
import { createBrowserHistory } from "history";
import { Settings } from "luxon";
import { useEffect, useState } from "react";
import {
  Admin,
  AuthContext,
  CustomRoutes,
  localStorageStore,
  StoreContextProvider,
  ThemeProvider,
  ThemesContext,
} from "react-admin";
import { Navigate, Route } from "react-router-dom";
import * as ReactDOM from "react-dom/client";
import { RecoilRoot, useRecoilValue } from "recoil";
import {
  authProvider,
  currentUser,
  grants,
  initAccessControl,
  restrictAccessToDrivingSchoolIds,
} from "./backoffice.access_control";
import { reportError } from "./backoffice.utils";
import { initFirebase } from "./firebase";
import { drivingSchoolsProvider, init as initDrivingSchoolsProvider } from "./providers/drivingSchoolsProvider";
import { i18nProvider } from "./l10n";
import { AccessDeniedPage } from "./misc/AccessDeniedPage";
import { BackofficeLayout } from "./misc/BackofficeLayout";
import { backofficeBrandForDrivingSchool, backofficeThemes } from "./misc/backofficeTheme";
import { LoginPage } from "./misc/LoginPage";
import { compositeDataProvider } from "./providers/compositeDataProvider";
import { instructorsProvider } from "./providers/instructorsProvider";
import { adasResource } from "./resources/adasResource";
import { areasResource } from "./resources/areasResource";
import { autovioEmployeesResource } from "./resources/autovioEmployeesResource";
import { bundlesResource } from "./resources/bundlesResource";
import { businessIntelligenceResource } from "./resources/businessIntelligenceResource";
import { drivingLicenseAuthoritiesResource } from "./resources/drivingLicenseAuthoritiesResource";
import { drivingSchoolsResource } from "./resources/drivingSchoolsResource";
import { dunningResource } from "./resources/dunningResource";
import { employeesResource } from "./resources/employeesResource";
import { instructorsResource } from "./resources/instructorsResource";
import { invoiceCustomersResource } from "./resources/invoiceCustomersResource";
import { productsResource } from "./resources/productsResource";
import { serviceEntriesResource } from "./resources/serviceEntriesResource";
import { promoCodesResource } from "./resources/promoCodesResource";
import { studentsResource } from "./resources/studentsResource";
import { theoryQuestionsResource } from "./resources/theoryQuestionsResource";
import { trainingsResource } from "./resources/trainingsResource";
import { usersToBeDeletedResource } from "./resources/usersToBeDeletedResource";
import { carsResource } from "./resources/carsResource";
import { motorcyclesResource } from "./resources/motorcyclesResource";
import { trailersResource } from "./resources/trailersResource";
import { simulatorsResource } from "./resources/simulatorsResource";
import { trucksResource } from "./resources/trucksResource";
import { busesResource } from "./resources/busesResource";
import { tractorsResource } from "./resources/tractorsResource";
import { vehiclesResource } from "./resources/vehiclesResource";
import { DrivingSchoolDashboard } from "./details/drivingSchool/DrivingSchoolDashboard.js";
import { rerenderCounterState } from "./hooks/useRerender";
import { persistentAutovioContext } from "./persistent_state";
import { appliedReferralCodesResource } from "./resources/appliedReferralCodesResource";
import { branchesResource } from "./resources/branchesResource";
import { playgroundResource } from "./resources/playgroundResource";
import { examLocationsResource } from "./resources/examLocationsResource";
import { drivingSchoolLabelsResource } from "./resources/drivingSchoolLabelsResource";
import { timetablesResource } from "./resources/timetablesResource";
import { courseTemplatesResource } from "./resources/courseTemplatesResource";
import { autoFormsResource } from "./resources/autoFormsResource";
import { CustomizationsFlagsUsage } from "./misc/CustomizationsFlagsUsage";
import { CalendarEventDispatcher } from "./misc/calendar/CalendarEventDispatcher";
import { MyCalendar } from "./misc/calendar/MyCalendar";
import { enableMapSet } from "immer";
import { testHelper } from "./utils/testHelper";
import { roomsResource } from "./resources/RoomsResource";
import { examAuthorityFeesResource } from "./resources/examAuthorityFeesResource";
import { drivingSchoolOtherEventCategoriesResource } from "./resources/drivingSchoolOtherEventCategoriesResource";
import { graduatedInvoiceCustomersResource } from "./resources/GraduatedInvoiceCustomersResource";
import { drivingSchoolServiceTypeResource } from "./resources/drivingSchoolServiceTypeResource";
import { subscriptionPackagesResource } from "./resources/subscriptionPackagesResource";
import { drivingSchoolCustomCardsResource } from "./resources/drivingSchoolCustomCardsResource";
import { getBackofficeLoginRedirectPath } from "./utils/navigation";
import { doNotSendStartupTrace, markMainStarted, markRenderingUi } from "./trackStartupPerformance";

import "@fontsource/poppins/400.css";
import "@fontsource/poppins/500.css";
import "@fontsource/poppins/600.css";
import "./css/academyTheme.css";
import "./css/autovioColors.css";
import "./css/calendar.css";
import "./css/dialogs.css";
import "./css/dunning.css";
import "./css/Form2.css";
import "./css/main.css";
import "./css/new_design.css";
import "./css/tailwind.css";

Settings.defaultZone = "Europe/Berlin";

const backofficeStore = localStorageStore();

async function main() {
  try {
    if (location.hostname === "autovio-dev.firebaseapp.com" || location.hostname === "autovio-dev.web.app") {
      const url = new URL(location.href);
      url.hostname = "backoffice.autovio.dev";
      location.replace(url);
      return;
    }
    if (location.hostname === "autovio-prod.firebaseapp.com" || location.hostname === "autovio-prod.web.app") {
      const url = new URL(location.href);
      url.hostname = "backoffice.autovio.de";
      location.replace(url);
      return;
    }
    {
      // Fix URL errors (see https://autovio.sentry.io/issues/5711412871)
      const match = /http[s]?:\//.exec(location.href.substring(1));
      if (match) {
        location.replace(location.href.substring(0, match.index + 1));
        return;
      }
    }
    markMainStarted();
    enableMapSet();
    window.testHelper = testHelper;
    window.runSafely = () => alert("runSafely is not initialized yet");
    window.handleError = () => alert("handleError is not initialized yet");
    initSentry();
    window.browserHistory = createBrowserHistory();
    await initFirebase();
    await initDrivingSchoolsProvider();
    // IMPORTANT: call `initAccessControl` *only after* `drivingSchoolsProvider` has been initialized.
    // `initAccessControl` needs the provider’s fetched/cached driving-school data.
    const { currentUser, grants } = await initAccessControl();
    window.dataProvider = compositeDataProvider;
    if (currentUser && grants.size > 0) {
      await drivingSchoolsProvider.preload();
      await instructorsProvider.preload();
    }
    const loginRedirectPath = getBackofficeLoginRedirectPath({
      pathname: browserHistory.location.pathname,
      isLoggedIn: !!currentUser,
    });
    if (loginRedirectPath) {
      browserHistory.replace(loginRedirectPath);
    }
    browserHistory.listen(({ action, location }: { action: "PUSH" | "POP" | "REPLACE"; location: Location }) => {
      console.info(`Navigation: ${action} ${location.pathname}`);
      window.onNavigation?.();
    });
    markRenderingUi();
    const root = ReactDOM.createRoot(document.getElementById("root")!);
    root.render(
      <RecoilRoot>
        <_VerifyAutovioContext>
          <Backoffice />
        </_VerifyAutovioContext>
      </RecoilRoot>,
    );
  } catch (error) {
    reportError("main() failed", error);
  }
}

void main();

function Backoffice() {
  const rerenderCounter = useRecoilValue(rerenderCounterState);
  const { drivingSchoolId } = useRecoilValue(persistentAutovioContext);
  const drivingSchoolIdFromPath = /^\/drivingSchools\/([^/]+)/.exec(location.pathname)?.[1];
  const restrictedDrivingSchoolId =
    restrictAccessToDrivingSchoolIds?.length === 1 ? restrictAccessToDrivingSchoolIds[0] : undefined;
  const brand = backofficeBrandForDrivingSchool(
    drivingSchoolIdFromPath ?? restrictedDrivingSchoolId ?? drivingSchoolId,
  );
  const themes = backofficeThemes[brand];
  const visibleResources = [];
  visibleResources.push(drivingSchoolsResource);
  visibleResources.push(branchesResource);
  visibleResources.push(instructorsResource);
  visibleResources.push(employeesResource);
  visibleResources.push(studentsResource);
  visibleResources.push(carsResource);
  visibleResources.push(motorcyclesResource);
  visibleResources.push(trailersResource);
  visibleResources.push(simulatorsResource);
  visibleResources.push(trucksResource);
  visibleResources.push(busesResource);
  visibleResources.push(tractorsResource);
  visibleResources.push(vehiclesResource);
  visibleResources.push(examLocationsResource);
  visibleResources.push(drivingSchoolLabelsResource);
  visibleResources.push(drivingSchoolOtherEventCategoriesResource);
  visibleResources.push(drivingSchoolServiceTypeResource);
  if (grants.has("developer") || grants.has("ServiceEntry:list")) {
    visibleResources.push(serviceEntriesResource);
  }
  visibleResources.push(drivingSchoolCustomCardsResource);
  if (grants.has("viewDunning")) {
    visibleResources.push(dunningResource);
  }
  if (grants.has("viewInvoiceCustomers")) {
    visibleResources.push(invoiceCustomersResource);
    visibleResources.push(graduatedInvoiceCustomersResource);
  }
  if (grants.has("manageDrivingLicenseAuthorities")) {
    visibleResources.push(drivingLicenseAuthoritiesResource);
  }
  if (grants.has("ExamAuthorityFee:list")) {
    visibleResources.push(examAuthorityFeesResource);
  }
  if (grants.has("viewAreas") && restrictAccessToDrivingSchoolIds?.length !== 1) {
    visibleResources.push(areasResource);
  }
  if (grants.has("viewProducts")) {
    visibleResources.push(productsResource);
  }
  if (grants.has("viewTrainings")) {
    visibleResources.push(trainingsResource);
  }
  if (grants.has("viewBundles") && restrictAccessToDrivingSchoolIds?.length !== 1) {
    visibleResources.push(bundlesResource);
  }
  if (grants.has("viewPromoCodes")) {
    visibleResources.push(promoCodesResource);
  }
  if (grants.has("viewAppliedReferralCodes")) {
    visibleResources.push(appliedReferralCodesResource);
  }
  if (grants.has("viewADAS")) {
    visibleResources.push(adasResource);
  }
  if (grants.has("viewAutovioEmployees")) {
    visibleResources.push(autovioEmployeesResource);
  }
  if (grants.has("viewUsersToBeDeleted")) {
    visibleResources.push(usersToBeDeletedResource);
  }
  if (grants.has("viewAutovioBusinessIntelligenceData") || grants.has("viewDrivingSchoolBusinessIntelligenceData")) {
    visibleResources.push(businessIntelligenceResource);
  }
  visibleResources.push(subscriptionPackagesResource);
  visibleResources.push(timetablesResource);
  visibleResources.push(courseTemplatesResource);
  visibleResources.push(autoFormsResource);
  visibleResources.push(roomsResource);
  visibleResources.push(theoryQuestionsResource);
  visibleResources.push(playgroundResource);
  if (!currentUser) {
    doNotSendStartupTrace();
    return (
      <AuthContext.Provider value={authProvider}>
        <StoreContextProvider value={backofficeStore}>
          <ThemesContext.Provider value={{ lightTheme: themes.light, darkTheme: themes.dark }}>
            <ThemeProvider>
              <LoginPage />
            </ThemeProvider>
          </ThemesContext.Provider>
        </StoreContextProvider>
      </AuthContext.Provider>
    );
  }
  if (currentUser && grants.size === 0) {
    doNotSendStartupTrace();
    return (
      <AuthContext.Provider value={authProvider}>
        <StoreContextProvider value={backofficeStore}>
          <ThemesContext.Provider value={{ lightTheme: themes.light, darkTheme: themes.dark }}>
            <ThemeProvider>
              <AccessDeniedPage />
            </ThemeProvider>
          </ThemesContext.Provider>
        </StoreContextProvider>
      </AuthContext.Provider>
    );
  }
  return (
    <LocalizationProvider
      localeText={deDE.components.MuiLocalizationProvider.defaultProps.localeText}
      dateAdapter={AdapterLuxon}
      adapterLocale="de"
    >
      <Admin
        key={rerenderCounter}
        lightTheme={themes.light}
        darkTheme={themes.dark}
        layout={BackofficeLayout}
        authProvider={authProvider}
        dataProvider={dataProvider}
        i18nProvider={i18nProvider}
        dashboard={DrivingSchoolDashboard}
        history={browserHistory}
        loginPage={LoginPage}
        store={backofficeStore}
        disableTelemetry
      >
        {visibleResources}
        <CustomRoutes>
          <Route path="/customization-flags-usage" element={<CustomizationsFlagsUsage />} />
          <Route path="/newDunning" element={<Navigate to="/dunning" />} />
          <Route path="/my-calendar" element={<MyCalendar />} />
          <Route path="/calendar-event/:calendarEventId" element={<CalendarEventDispatcher />} />
        </CustomRoutes>
      </Admin>
    </LocalizationProvider>
  );
}

function _VerifyAutovioContext({ children }: { children: React.ReactNode }) {
  const { drivingSchoolId } = useRecoilValue(persistentAutovioContext);
  const [verified, setVerified] = useState(false);
  useEffect(() => {
    if (drivingSchoolId) {
      const drivingSchool = drivingSchoolsProvider.getOneFromCache(drivingSchoolId);
      if (!drivingSchool) {
        console.info(`Unknown driving school in autovioContext: ${drivingSchoolId} -- clearing storage ...`);
        let storageCleared = false;
        try {
          _clearStorage();
          storageCleared = true;
        } catch (error) {
          console.error("Failed to clear storage", error);
        }
        if (storageCleared) {
          console.info("Reloading page ...");
          location.reload();
        }
      }
    }
    setVerified(true);
  }, []);
  return verified ? children : null;
}

function _clearStorage(): void {
  localStorage.clear();
  sessionStorage.clear();
}
